import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const { FiMenu, FiX, FiHeart, FiLogOut, FiSettings } = FiIcons;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', href: 'hero' },
    { name: 'About', href: 'about' },
    { name: 'Team', href: 'team' }, // Added Team link
    { name: 'Services', href: 'services' },
    { name: 'Events', href: 'events' },
    { name: 'Gallery', href: 'gallery' },
    { name: 'Contact', href: 'contact' }
  ];

  const handleNavClick = (href) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(href)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(href)?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div 
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => handleNavClick('hero')}
          >
            <SafeIcon icon={FiHeart} className="h-8 w-8 text-rose-500" />
            <span className="text-2xl font-bold bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent">
              Canvas & Clarity
            </span>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavClick(item.href)}
                className="text-gray-700 hover:text-rose-600 font-medium transition-colors"
              >
                {item.name}
              </button>
            ))}
            
            {user ? (
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => navigate('/admin')}
                  className="p-2 text-rose-500 hover:bg-rose-50 rounded-full"
                >
                  <SafeIcon icon={FiSettings} />
                </button>
                <button 
                  onClick={logout}
                  className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full font-medium hover:bg-gray-200"
                >
                  <SafeIcon icon={FiLogOut} />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => handleNavClick('events')}
                className="bg-gradient-to-r from-rose-500 to-purple-600 text-white px-6 py-2.5 rounded-full font-medium shadow-lg hover:shadow-xl transition-all"
              >
                Book Now
              </button>
            )}
          </div>

          <div className="md:hidden flex items-center space-x-4">
            {user && (
              <button onClick={() => navigate('/admin')} className="p-2 text-rose-500">
                <SafeIcon icon={FiSettings} />
              </button>
            )}
            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-700">
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-6 w-6" />
            </button>
          </div>
        </div>

        <motion.div
          initial={false}
          animate={{ height: isOpen ? 'auto' : 0 }}
          className="md:hidden overflow-hidden bg-white rounded-b-2xl shadow-xl"
        >
          <div className="px-4 py-6 space-y-4">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavClick(item.href)}
                className="block w-full text-left text-lg text-gray-700 font-medium"
              >
                {item.name}
              </button>
            ))}
            {!user && (
              <button 
                onClick={() => handleNavClick('events')}
                className="w-full bg-rose-500 text-white py-3 rounded-xl font-bold"
              >
                Book Now
              </button>
            )}
            {user && (
              <button 
                onClick={logout}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-bold flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiLogOut} />
                <span>Logout</span>
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default Navbar;